import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiTruck, FiSearch, FiDownload, FiPrinter,
  FiBarChart2, FiDollarSign, FiArrowUp, FiArrowDown
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { formatCurrency, formatNumber, formatDate as fmtDate } from '../../utils/formatters';
import { recordReads } from '../../utils/readTelemetry';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import VendorSelect from '../../components/VendorSelect';
import { addPrintBrand } from '../../utils/printBranding';

const formatAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const formatDate = (v) => fmtDate(v) || '-';

const VendorBalances = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const navigate = useNavigate();

  // Data state
  const [accounts, setAccounts] = useState([]);
  const [subTransactions, setSubTransactions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  // Filter state
  const [selectedFYId, setSelectedFYId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedVendorId, setSelectedVendorId] = useState('all');
  const [selectedVendorType, setSelectedVendorType] = useState('all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [minBalance, setMinBalance] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Load financial years
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
        const activeFY = fys.find(f => f.id === activeFinancialYear.id);
        if (activeFY) {
          setDateFrom(activeFY.startDate || '');
        }
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
        setDateFrom(fys[0].startDate || '');
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  // Load vendors
  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'vendors');
      const snapshot = await getDocs(ref);
      recordReads(activeCompany.id, snapshot.size);
      setVendors(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  }, [activeCompany]);

  // Load accounts (vendor linked)
  const loadAccounts = useCallback(async () => {
    if (!activeCompany?.id) return [];
    try {
      const accountsRef = collection(db, 'companies', activeCompany.id, 'accounts');
      const q = query(accountsRef, where('linkedEntityType', '==', 'vendor'));
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error loading accounts:', error);
      return [];
    }
  }, [activeCompany]);

  // Load subTransactions
  const loadSubTransactions = useCallback(async () => {
    if (!activeCompany?.id || !selectedFYId) return [];
    try {
      const subTransRef = collection(db, 'companies', activeCompany.id, 'subTransactions');
      const q = query(
        subTransRef,
        where('status', 'in', ['Posted', 'Draft']),
        where('financialYearId', '==', selectedFYId)
      );
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(t => {
          // Include Posted entries always, and Draft entries only from OB (Opening Balance) transactions
          if (t.status === 'Draft' && t.tranTypeCode !== 'OB') return false;
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });
    } catch (error) {
      console.error('Error loading subTransactions:', error);
      return [];
    }
  }, [activeCompany, selectedFYId, activeBranch]);

  useEffect(() => {
    loadFinancialYears();
    loadVendors();
  }, [loadFinancialYears, loadVendors]);

  // Update dateFrom when FY changes
  useEffect(() => {
    const fy = financialYears.find(f => f.id === selectedFYId);
    if (fy) {
      setDateFrom(fy.startDate || '');
    }
  }, [selectedFYId, financialYears]);

  // Generate report
  const handleGenerateReport = async () => {
    if (!activeCompany?.id || !selectedFYId) return;
    setLoading(true);
    setHasGenerated(true);
    try {
      const [accs, trans] = await Promise.all([
        loadAccounts(),
        loadSubTransactions()
      ]);
      setAccounts(accs);
      setSubTransactions(trans);
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setLoading(false);
    }
  };

  // Build vendor map
  const vendorMap = useMemo(() => {
    const map = {};
    vendors.forEach(v => { map[v.id] = v; });
    return map;
  }, [vendors]);

  // Build accountId → vendorId mapping
  const accountToVendor = useMemo(() => {
    const map = {};
    accounts.forEach(acc => {
      if (acc.linkedEntityId) {
        map[acc.id] = acc.linkedEntityId;
      }
    });
    return map;
  }, [accounts]);

  // Build vendorId → accountId mapping
  const vendorToAccount = useMemo(() => {
    const map = {};
    accounts.forEach(acc => {
      if (acc.linkedEntityId) {
        map[acc.linkedEntityId] = acc.id;
      }
    });
    return map;
  }, [accounts]);

  // Set of vendor account IDs
  const vendorAccountIds = useMemo(() => {
    return new Set(accounts.map(a => a.id));
  }, [accounts]);

  // Unique vendor types and cities for filters
  const vendorTypes = useMemo(() => {
    const types = new Set();
    vendors.forEach(v => {
      if (v.vendorType) types.add(v.vendorType);
    });
    return Array.from(types).sort();
  }, [vendors]);

  const vendorCities = useMemo(() => {
    const cities = new Set();
    vendors.forEach(v => {
      if (v.city) cities.add(v.city);
    });
    return Array.from(cities).sort();
  }, [vendors]);

  // Compute balances per vendor
  // Vendor accounts are liability-type: credit increases balance
  const vendorBalances = useMemo(() => {
    const balances = {};

    // Initialize all vendors that have linked accounts
    accounts.forEach(acc => {
      if (acc.linkedEntityId) {
        balances[acc.linkedEntityId] = { opening: 0, debit: 0, credit: 0 };
      }
    });

    // Process sub-transactions
    subTransactions.forEach(t => {
      if (!vendorAccountIds.has(t.accountId)) return;
      const vendorId = accountToVendor[t.accountId];
      if (!vendorId || !balances[vendorId]) return;

      const docDate = t.docDate || '';

      if (dateFrom && docDate < dateFrom) {
        // Before date range → opening balance (liability account: credit - debit)
        balances[vendorId].opening += (t.creditAmount || 0) - (t.debitAmount || 0);
      } else if ((!dateFrom || docDate >= dateFrom) && (!dateTo || docDate <= dateTo)) {
        // Within date range
        balances[vendorId].debit += (t.debitAmount || 0);
        balances[vendorId].credit += (t.creditAmount || 0);
      }
    });

    return balances;
  }, [subTransactions, accounts, vendorAccountIds, accountToVendor, dateFrom, dateTo]);

  // Build report rows
  const reportRows = useMemo(() => {
    const rows = [];

    Object.keys(vendorBalances).forEach(vendorId => {
      const vendor = vendorMap[vendorId];
      if (!vendor) return;

      const bal = vendorBalances[vendorId];
      // Liability: closing = opening + credit - debit
      const closing = bal.opening + bal.credit - bal.debit;

      rows.push({
        vendorId,
        accountId: vendorToAccount[vendorId],
        name: vendor.vendorName || vendor.companyName || '',
        vendorType: vendor.vendorType || '',
        city: vendor.city || '',
        phone: vendor.phone || vendor.mobile || '',
        opening: bal.opening,
        debit: bal.debit,
        credit: bal.credit,
        closing
      });
    });

    return rows;
  }, [vendorBalances, vendorMap, vendorToAccount]);

  // Apply filters and search
  const filteredRows = useMemo(() => {
    let rows = [...reportRows];

    // Vendor filter
    if (selectedVendorId !== 'all') {
      rows = rows.filter(r => r.vendorId === selectedVendorId);
    }

    // Vendor type filter
    if (selectedVendorType !== 'all') {
      rows = rows.filter(r => r.vendorType === selectedVendorType);
    }

    // City filter
    if (selectedCity !== 'all') {
      rows = rows.filter(r => r.city === selectedCity);
    }

    // Min balance
    if (minBalance !== '' && !isNaN(Number(minBalance))) {
      const threshold = Number(minBalance);
      rows = rows.filter(r => Math.abs(r.closing) >= threshold);
    }

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter(r =>
        r.name.toLowerCase().includes(q) ||
        r.vendorType.toLowerCase().includes(q) ||
        r.city.toLowerCase().includes(q)
      );
    }

    // Sort
    rows.sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case 'name': valA = a.name.toLowerCase(); valB = b.name.toLowerCase(); break;
        case 'type': valA = a.vendorType.toLowerCase(); valB = b.vendorType.toLowerCase(); break;
        case 'city': valA = a.city.toLowerCase(); valB = b.city.toLowerCase(); break;
        case 'opening': valA = a.opening; valB = b.opening; break;
        case 'debit': valA = a.debit; valB = b.debit; break;
        case 'credit': valA = a.credit; valB = b.credit; break;
        case 'closing': valA = a.closing; valB = b.closing; break;
        default: valA = a.name.toLowerCase(); valB = b.name.toLowerCase();
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return rows;
  }, [reportRows, selectedVendorId, selectedVendorType, selectedCity, minBalance, searchQuery, sortField, sortDir]);

  // Summary
  const summary = useMemo(() => {
    let totalPayable = 0;
    let totalDebit = 0;
    let totalCredit = 0;
    let count = 0;

    filteredRows.forEach(row => {
      totalPayable += row.closing;
      totalDebit += row.debit;
      totalCredit += row.credit;
      if (Math.abs(row.closing) > 0.01) count++;
    });

    return { totalVendors: count, totalPayable, totalDebit, totalCredit };
  }, [filteredRows]);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />;
  };

  // Navigate to ledger for vendor
  const handleVendorClick = (row) => {
    if (row.accountId) {
      navigate('/reports/ledger', {
        state: { filterAccountId: row.accountId, filterAccountName: row.name }
      });
    }
  };

  // CSV Export
  const handleExport = () => {
    if (filteredRows.length === 0) return;
    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = 'Vendor Balances Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `Period: ${formatDate(dateFrom)} to ${formatDate(dateTo)}\n`;
    csv += `Generated: ${formatDate(new Date().toISOString().split('T')[0])}\n\n`;
    csv += '#,Vendor,Type,City,Opening,Debit,Credit,Closing\n';

    filteredRows.forEach((row, i) => {
      csv += `${i + 1},"${row.name.replace(/"/g, '""')}","${row.vendorType}","${row.city}",`;
      csv += `${row.opening.toFixed(2)},${row.debit.toFixed(2)},${row.credit.toFixed(2)},${row.closing.toFixed(2)}\n`;
    });

    csv += `\n,,,,${filteredRows.reduce((s, r) => s + r.opening, 0).toFixed(2)},${summary.totalDebit.toFixed(2)},${summary.totalCredit.toFixed(2)},${summary.totalPayable.toFixed(2)}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Vendor_Balances_${dateFrom}_to_${dateTo}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Vendor Balances</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
        h1{color:#800020;font-size:22px;margin-bottom:5px}
        .meta{color:#666;font-size:12px;margin-bottom:20px}
        .summary{display:flex;gap:30px;margin-bottom:20px;padding:15px;background:#f9f9f9;border-radius:8px}
        .summary-item{text-align:center}
        .summary-label{font-size:11px;color:#666;margin-bottom:4px}
        .summary-value{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:8px 12px;text-align:left;font-size:11px;text-transform:uppercase}
        th.amount{text-align:right}
        td{padding:6px 12px;border-bottom:1px solid #ddd;font-size:12px}
        td.amount{text-align:right;font-family:'Consolas',monospace}
        .total-row{background:#800020;color:white;font-weight:700;font-size:13px}
        .total-row td{border-bottom:none;padding:10px 12px}
        .positive{color:#1565C0} .negative{color:#C62828}
        @media print{body{margin:15px}}
      </style></head><body>
      <h1>Vendor Balances</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        Period: ${formatDate(dateFrom)} to ${formatDate(dateTo)} |
        Generated: ${formatDate(new Date().toISOString().split('T')[0])}
      </div>
      <div class="summary">
        <div class="summary-item">
          <div class="summary-label">Vendors with Balance</div>
          <div class="summary-value">${summary.totalVendors}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total Payable</div>
          <div class="summary-value">${formatAmount(summary.totalPayable)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total Debit</div>
          <div class="summary-value">${formatAmount(summary.totalDebit)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total Credit</div>
          <div class="summary-value">${formatAmount(summary.totalCredit)}</div>
        </div>
      </div>
      <table>
        <thead><tr>
          <th>#</th>
          <th>Vendor</th>
          <th>Type</th>
          <th>City</th>
          <th class="amount">Opening</th>
          <th class="amount">Debit</th>
          <th class="amount">Credit</th>
          <th class="amount">Closing</th>
        </tr></thead>
        <tbody>
          ${filteredRows.map((row, i) => `<tr>
            <td>${i + 1}</td>
            <td>${row.name}</td>
            <td>${row.vendorType}</td>
            <td>${row.city}</td>
            <td class="amount">${formatAmount(row.opening)}</td>
            <td class="amount">${formatAmount(row.debit)}</td>
            <td class="amount">${formatAmount(row.credit)}</td>
            <td class="amount ${row.closing >= 0 ? 'positive' : 'negative'}">${formatAmount(row.closing)}</td>
          </tr>`).join('')}
          <tr class="total-row">
            <td colspan="4" style="text-align:right">Grand Total</td>
            <td class="amount">${formatAmount(filteredRows.reduce((s, r) => s + r.opening, 0))}</td>
            <td class="amount">${formatAmount(summary.totalDebit)}</td>
            <td class="amount">${formatAmount(summary.totalCredit)}</td>
            <td class="amount">${formatAmount(summary.totalPayable)}</td>
          </tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  return (
    <PageLayout pageTitle="Vendor Balances" breadcrumb="Reports / Purchase Reports / Vendor Balances">
      {/* Filters */}
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Date From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>Date To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>Vendor</Label>
            <VendorSelect
              vendors={vendors}
              selectedVendorId={selectedVendorId}
              onChange={setSelectedVendorId}
              allOption={{ value: 'all', label: 'All Vendors' }}
            />
          </FilterGroup>
        </FiltersRow>
        <FiltersRow style={{ marginTop: 14 }}>
          <FilterGroup>
            <Label>Vendor Type</Label>
            <Select value={selectedVendorType} onChange={e => setSelectedVendorType(e.target.value)}>
              <option value="all">All Types</option>
              {vendorTypes.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>City</Label>
            <Select value={selectedCity} onChange={e => setSelectedCity(e.target.value)}>
              <option value="all">All Cities</option>
              {vendorCities.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Min Balance</Label>
            <Input
              type="number"
              placeholder="0"
              value={minBalance}
              onChange={e => setMinBalance(e.target.value)}
              style={{ width: 120 }}
            />
          </FilterGroup>
          <SearchWrap>
            <SearchIcon><FiSearch /></SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search vendor name..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </SearchWrap>
          <ButtonGroup>
            <Button onClick={handleExport} disabled={filteredRows.length === 0}>
              <FiDownload /> CSV
            </Button>
            <Button onClick={handlePrint} disabled={filteredRows.length === 0}>
              <FiPrinter /> Print
            </Button>
            <Button primary onClick={handleGenerateReport} disabled={loading || !selectedFYId}>
              <FiBarChart2 /> {loading ? 'Loading...' : 'Generate'}
            </Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      {/* Summary Cards */}
      {hasGenerated && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIcon color={colors.primary.main}><FiTruck /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Vendors with Balance</SummaryLabel>
              <SummaryValue>{formatNumber(summary.totalVendors)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon color="#C62828"><FiDollarSign /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Payable</SummaryLabel>
              <SummaryValue style={{ color: '#C62828' }}>{formatCurrency(summary.totalPayable)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon color={colors.success.main}>
              <span style={{ fontSize: 16, fontWeight: 700 }}>Dr</span>
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Debit</SummaryLabel>
              <SummaryValue style={{ color: colors.success.main }}>{formatCurrency(summary.totalDebit)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon color="#1565C0">
              <span style={{ fontSize: 16, fontWeight: 700 }}>Cr</span>
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Credit</SummaryLabel>
              <SummaryValue style={{ color: '#1565C0' }}>{formatCurrency(summary.totalCredit)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* Table */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyIcon><FiBarChart2 /></EmptyIcon>
              <EmptyText>Loading vendor balances...</EmptyText>
            </EmptyState>
          ) : !hasGenerated ? (
            <EmptyState>
              <EmptyIcon><FiTruck /></EmptyIcon>
              <EmptyText>Select a financial year and click Generate to view vendor balances</EmptyText>
            </EmptyState>
          ) : filteredRows.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiDollarSign /></EmptyIcon>
              <EmptyText>No vendor balances found for the selected criteria</EmptyText>
              <p style={{ fontSize: '14px', color: colors.text.tertiary, marginTop: 8 }}>
                Try adjusting your filters or date range
              </p>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th style={{ width: 50 }}>#</Th>
                  <Th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                    Vendor <SortIcon field="name" />
                  </Th>
                  <Th onClick={() => handleSort('type')} style={{ cursor: 'pointer' }}>
                    Type <SortIcon field="type" />
                  </Th>
                  <Th onClick={() => handleSort('city')} style={{ cursor: 'pointer' }}>
                    City <SortIcon field="city" />
                  </Th>
                  <Th align="right" onClick={() => handleSort('opening')} style={{ cursor: 'pointer', width: 140 }}>
                    Opening <SortIcon field="opening" />
                  </Th>
                  <Th align="right" onClick={() => handleSort('debit')} style={{ cursor: 'pointer', width: 140 }}>
                    Debit <SortIcon field="debit" />
                  </Th>
                  <Th align="right" onClick={() => handleSort('credit')} style={{ cursor: 'pointer', width: 140 }}>
                    Credit <SortIcon field="credit" />
                  </Th>
                  <Th align="right" onClick={() => handleSort('closing')} style={{ cursor: 'pointer', width: 140 }}>
                    Closing <SortIcon field="closing" />
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredRows.map((row, index) => (
                  <Tr key={row.vendorId}>
                    <Td>{index + 1}</Td>
                    <Td>
                      <ClickableName onClick={() => handleVendorClick(row)}>
                        {row.name}
                      </ClickableName>
                    </Td>
                    <Td><TypeBadge>{row.vendorType}</TypeBadge></Td>
                    <Td>{row.city}</Td>
                    <AmountCell align="right">
                      {row.opening !== 0 ? formatAmount(row.opening) : '-'}
                    </AmountCell>
                    <AmountCell align="right">
                      {row.debit > 0 ? formatAmount(row.debit) : '-'}
                    </AmountCell>
                    <AmountCell align="right">
                      {row.credit > 0 ? formatAmount(row.credit) : '-'}
                    </AmountCell>
                    <AmountCell align="right" $isClosing $positive={row.closing >= 0}>
                      {formatAmount(row.closing)}
                    </AmountCell>
                  </Tr>
                ))}
              </Tbody>
              <Tfoot>
                <TotalRow>
                  <Td colSpan={4} style={{ textAlign: 'right', fontWeight: 700, fontSize: 14 }}>
                    Grand Total
                  </Td>
                  <AmountCell align="right" $isTotal>
                    {formatAmount(filteredRows.reduce((s, r) => s + r.opening, 0))}
                  </AmountCell>
                  <AmountCell align="right" $isTotal>
                    {formatAmount(summary.totalDebit)}
                  </AmountCell>
                  <AmountCell align="right" $isTotal>
                    {formatAmount(summary.totalCredit)}
                  </AmountCell>
                  <AmountCell align="right" $isTotal>
                    {formatAmount(summary.totalPayable)}
                  </AmountCell>
                </TotalRow>
              </Tfoot>
            </Table>
          )}
        </TableContainer>
      </ContentCard>
    </PageLayout>
  );
};

// Styled Components
const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 150px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;
  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 220px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.text.secondary};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 9px 12px 9px 38px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;
  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
  &::placeholder { color: ${colors.text.tertiary}; }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button`
  padding: 9px 14px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  white-space: nowrap;

  ${props => props.primary ? `
    background: ${colors.primary.main};
    color: white;
    border-color: ${colors.primary.main};
    &:hover:not(:disabled) {
      background: ${colors.primary.dark};
      border-color: ${colors.primary.dark};
    }
  ` : `
    background: white;
    color: ${colors.text.primary};
    &:hover:not(:disabled) {
      background: ${colors.neutral[100]};
      border-color: ${colors.primary.main};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.color || colors.primary.main}15;
  color: ${props => props.color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 14px;
  text-align: ${props => props.align || 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  &:hover {
    color: ${colors.primary.main};
  }
`;

const Tbody = styled.tbody``;
const Tfoot = styled.tfoot``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;
  &:hover { background: ${colors.neutral[50]}; }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  text-align: right;
  white-space: nowrap;

  ${props => props.$isTotal && `
    font-weight: 700;
    font-size: 14px;
    color: ${colors.text.primary};
  `}

  ${props => props.$isClosing && `
    font-weight: 600;
    color: ${props.$positive ? '#1565C0' : '#C62828'};
  `}
`;

const ClickableName = styled.span`
  cursor: pointer;
  color: #1a73e8;
  &:hover {
    text-decoration: underline;
  }
`;

const TypeBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 3px;
  background: ${colors.primary.main}10;
  color: ${colors.primary.main};
`;

const TotalRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.primary.main};
  td { padding: 14px; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default VendorBalances;
